/* eslint-disable consistent-return */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/require-default-props */
import React, {
   useEffect,
   useMemo,
   useRef,
   useState,
   useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { IconButton } from '../../styles/ui/Button';

const ANIM_MS = 220;

const Overlay = styled.div`
   position: fixed;
   inset: 0;

   background: rgba(0, 0, 0, 0.35);
   backdrop-filter: blur(6px);
   -webkit-backdrop-filter: blur(6px);

   display: flex;
   align-items: center;
   justify-content: center;
   padding: 16px;
   z-index: 9999;

   opacity: ${(p) => (p.$show ? 1 : 0)};
   transition: opacity ${ANIM_MS}ms ease;

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

const Dialog = styled.div`
   width: 100%;
   max-width: ${(p) => p.$maxWidth || '820px'};
   background: #fff;
   border-radius: 14px;
   box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
   overflow: hidden;

   opacity: ${(p) => (p.$show ? 1 : 0)};
   transform: ${(p) =>
      p.$show ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.94)'};

   transition:
      opacity ${ANIM_MS}ms ease,
      transform ${ANIM_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1);

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

const Header = styled.div`
   padding: 14px 16px;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.08);

   .modal-heading {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
   }

   /* Slot de ícone do título (FC7 cap.2 §H.2.1 — prop titleIcon). Opcional e
      retrocompatível: sem a prop, o header não muda em nada. */
   .modal-title-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 38px;
      height: 38px;
      border-radius: 10px;
      background: rgba(64, 152, 239, 0.12);
      color: #2f7bd8;
   }

   h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 800;
   }

   .modal-subtitle {
      margin: 2px 0 0;
      font-size: 0.8rem;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.55);
   }
`;

const CloseButton = styled(IconButton)`
   font-size: 22px;
   line-height: 1;
`;

const Body = styled.div`
   padding: 16px;
`;

const Footer = styled.div`
   padding: 12px 16px;
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   border-top: 1px solid rgba(0, 0, 0, 0.08);

   button {
      min-height: 36px;
   }
`;

function getFocusableElements(container) {
   if (!container) return [];
   return Array.from(
      container.querySelectorAll(
         'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
   );
}

export default function Modal({
   isOpen,
   title,
   titleIcon,
   subtitle,
   onClose,
   children,
   footer,
   maxWidth,
}) {
   const dialogRef = useRef(null);
   const previouslyFocused = useRef(null);

   const [isMounted, setIsMounted] = useState(isOpen);
   const [show, setShow] = useState(false);

   // -------------------------------------------------------------------------
   // Controla montagem/desmontagem com animação de entrada e saída.
   // -------------------------------------------------------------------------
   useEffect(() => {
      if (isOpen) {
         setIsMounted(true);
         window.setTimeout(() => setShow(true), 0);
         return;
      }

      if (isMounted) {
         setShow(false);
         const animationTimer = window.setTimeout(
            () => setIsMounted(false),
            ANIM_MS
         );
         return () => window.clearTimeout(animationTimer);
      }
   }, [isOpen, isMounted]);

   // -------------------------------------------------------------------------
   // Controla overflow do body com base em isOpen — useEffect dedicado e
   // independente, sem risco de condição de corrida com o ciclo de isMounted.
   //
   // Problema anterior: prevOverflow era capturado dentro do useEffect[isMounted],
   // que rodava seu cleanup múltiplas vezes durante o ciclo de animação. Se
   // prevOverflow fosse 'hidden' (capturado em re-render com modal ainda aberto),
   // o cleanup restaurava 'hidden' em vez de '', travando o scroll da página.
   // -------------------------------------------------------------------------
   useEffect(() => {
      if (!isOpen) return undefined;

      const overflowBeforeModalOpen = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';

      return () => {
         document.body.style.overflow = overflowBeforeModalOpen;
      };
   }, [isOpen]);

   const requestClose = useCallback(() => {
      setShow(false);
      onClose?.();
   }, [onClose]);

   // Mantém o requestClose atual num ref para o handler de teclado do focus-trap
   // NÃO depender da identidade dele. Sem isso, quando o pai passa um onClose
   // recriado a cada render (ex.: um wrapper `() => ...`), requestClose muda de
   // identidade, o efeito de foco re-roda a cada tecla e ROUBA o foco do input
   // (bug clássico dos modais). O efeito passa a depender só de isMounted.
   const requestCloseRef = useRef(requestClose);
   useEffect(() => {
      requestCloseRef.current = requestClose;
   }, [requestClose]);

   const portalTarget = useMemo(() => document.body, []);

   // -------------------------------------------------------------------------
   // Gerencia foco (trap + restauração) e atalhos de teclado (Escape, Tab).
   // Depende de isMounted para manter o listener ativo durante a animação
   // de saída (o modal ainda está visível até desmontagem completa).
   // -------------------------------------------------------------------------
   useEffect(() => {
      if (!isMounted) return undefined;

      previouslyFocused.current = document.activeElement;

      const focusTimer = window.setTimeout(() => {
         const focusableElements = getFocusableElements(dialogRef.current);
         if (focusableElements.length) focusableElements[0].focus();
      }, 0);

      const onKeyDown = (e) => {
         if (e.key === 'Escape') {
            requestCloseRef.current();
            return;
         }

         if (e.key === 'Tab') {
            const focusableElements = getFocusableElements(dialogRef.current);
            if (!focusableElements.length) return;

            const firstFocusable = focusableElements[0];
            const lastFocusable =
               focusableElements[focusableElements.length - 1];

            if (e.shiftKey && document.activeElement === firstFocusable) {
               e.preventDefault();
               lastFocusable.focus();
            } else if (
               !e.shiftKey &&
               document.activeElement === lastFocusable
            ) {
               e.preventDefault();
               firstFocusable.focus();
            }
         }
      };

      window.addEventListener('keydown', onKeyDown);

      return () => {
         window.clearTimeout(focusTimer);
         window.removeEventListener('keydown', onKeyDown);

         if (previouslyFocused.current?.focus) {
            previouslyFocused.current.focus();
         }
      };
   }, [isMounted]);

   if (!isMounted) return null;

   return createPortal(
      <Overlay
         $show={show}
         role="presentation"
         onMouseDown={(e) => {
            if (e.target === e.currentTarget) requestClose();
         }}
      >
         <Dialog
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={title || 'Modal'}
            $maxWidth={maxWidth}
            $show={show}
         >
            <Header>
               <div className="modal-heading">
                  {titleIcon ? (
                     <span className="modal-title-icon" aria-hidden="true">
                        {titleIcon}
                     </span>
                  ) : null}
                  <div>
                     <h3>{title}</h3>
                     {subtitle ? (
                        <p className="modal-subtitle">{subtitle}</p>
                     ) : null}
                  </div>
               </div>
               <CloseButton
                  type="button"
                  onClick={requestClose}
                  aria-label="Fechar"
               >
                  X
               </CloseButton>
            </Header>

            <Body>{children}</Body>

            {footer ? <Footer>{footer}</Footer> : null}
         </Dialog>
      </Overlay>,
      portalTarget
   );
}

Modal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   title: PropTypes.string,
   titleIcon: PropTypes.node,
   subtitle: PropTypes.node,
   onClose: PropTypes.func,
   children: PropTypes.node,
   footer: PropTypes.node,
   maxWidth: PropTypes.string,
};
